'use client';

import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';

/**
 * The floating layer every kit menu opens in: FvdSelect's list, the table's
 * Columns and filter menus.
 *
 * Those used to hang off their trigger with `position: absolute`, so any
 * ancestor that clipped (a dialog body that scrolls, a Setup card mid
 * animation, a table wrapper) cut the list off: the Gender dropdown in "Add a
 * division" opened as a sliver inside the dialog. Portalled to <body> and
 * placed with `position: fixed` against the trigger, nothing can clip it.
 *
 * It opens below the trigger, or above it when there is not room below and
 * there is more room above. It follows the trigger when anything scrolls (the
 * dashboard scrolls <main>, a dialog scrolls its body) and on resize, and sits
 * above dialogs (FvdWindow is 1000).
 *
 * `pointer-events: auto` because a modal Radix dialog sets `none` on <body>,
 * and a list portalled there would otherwise be unclickable. Clicks in it
 * still count as inside the dialog: React events bubble through the portal.
 */

export const FVD_POPOVER_Z = 1100;

/**
 * Where floating menus open on a themed page (an event's own page): a layer
 * PhotoPageSkin adds to <body>, beside the page rather than inside it, that
 * carries the page's theme variables, its dark class and its body font. So a
 * menu wears the event's colours and font in light and dark, sits above the
 * page's own stacking context (.themed-page is z-index 1), and takes none of
 * the page's element rules (.themed-page input, .card-bg). Null: <body>.
 */
export const FvdPortalHostContext = createContext<HTMLElement | null>(null);

/** The layer a menu portals into: the themed page's, else <body>. */
export function useFvdPortalHost(): HTMLElement | null {
  return useContext(FvdPortalHostContext);
}

interface Placement {
  top: number;
  left?: number;
  right?: number;
  minWidth: number;
  maxWidth: number;
  above: boolean;
}

export function useFvdPlacement(
  anchor: RefObject<HTMLElement | null>,
  open: boolean,
  /** Roughly how tall it opens, to decide whether it fits below. */
  height: number,
  align: 'start' | 'end' = 'start',
): Placement | null {
  const [pos, setPos] = useState<Placement | null>(null);
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const el = anchor.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const vw = window.innerWidth;
      const room = window.innerHeight - r.bottom;
      const above = room < height + 12 && r.top > room;
      const top = above ? r.top - 4 : r.bottom + 4;
      if (align === 'end') {
        const right = Math.max(8, vw - r.right);
        setPos({ top, right, minWidth: Math.min(r.width, vw - 16), maxWidth: vw - right - 8, above });
      } else {
        const left = Math.max(8, Math.min(r.left, vw - Math.min(r.width, vw - 16) - 8));
        setPos({ top, left, minWidth: Math.min(r.width, vw - 16), maxWidth: vw - left - 8, above });
      }
    };
    place();
    window.addEventListener('resize', place);
    // Capture, so a scroll inside any container moves it with its trigger.
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, anchor, height, align]);
  return open ? pos : null;
}

export function FvdPopover({
  anchor,
  open,
  onClose,
  height,
  align = 'start',
  minWidth,
  role,
  label,
  style,
  children,
}: {
  /** The trigger, or the wrapper round it: clicks inside it do not close the menu. */
  anchor: RefObject<HTMLElement | null>;
  open: boolean;
  onClose: () => void;
  height: number;
  /** 'end' lines its right edge up with the trigger's (a menu off a right-hand button). */
  align?: 'start' | 'end';
  /** At least this wide, as well as at least the trigger's width. */
  minWidth?: number;
  role?: string;
  label?: string;
  style?: CSSProperties;
  children: ReactNode;
}) {
  const pos = useFvdPlacement(anchor, open, height, align);
  const themedHost = useFvdPortalHost();
  const box = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  useEffect(() => {
    close.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    const away = (e: Event) => {
      const t = e.target as Node;
      if (anchor.current?.contains(t) || box.current?.contains(t)) return;
      close.current();
    };
    // Escape closes the menu and only the menu: caught first and stopped, so
    // a dialog round it (which also closes on Escape) stays open. On window,
    // in the capture phase, because that runs before anything on document:
    // a Radix dialog listens on document in capture too, and won the race.
    const key = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      close.current();
    };
    document.addEventListener('mousedown', away);
    document.addEventListener('touchstart', away);
    window.addEventListener('keydown', key, true);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('touchstart', away);
      window.removeEventListener('keydown', key, true);
    };
  }, [open, anchor]);

  if (!open || !pos || typeof document === 'undefined') return null;
  const host = themedHost ?? document.body;
  return createPortal(
    <div
      ref={box}
      role={role}
      aria-label={label}
      style={{
        boxSizing: 'border-box',
        borderRadius: 'var(--fvd-radius)',
        border: '1px solid var(--fvd-hairline-strong)',
        background: 'var(--fvd-elevated, var(--fvd-canvas))',
        // The one shadow the system allows itself, and only because a menu
        // floating over content has to read as floating.
        boxShadow: 'var(--fvd-shadow-dropdown)',
        color: 'var(--fvd-ink)',
        // On an event's own page, its body face (inherited from the host).
        fontFamily: host === document.body ? 'var(--fvd-font-body, inherit)' : 'inherit',
        padding: 4,
        ...style,
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        right: pos.right,
        minWidth: Math.min(Math.max(pos.minWidth, minWidth ?? 0), pos.maxWidth),
        maxWidth: pos.maxWidth,
        transform: pos.above ? 'translateY(-100%)' : undefined,
        zIndex: FVD_POPOVER_Z,
        pointerEvents: 'auto',
      }}
    >
      {children}
    </div>,
    host,
  );
}
